/**
 * Mermaid Diagram Templates and Configuration
 * 
 * Shared configuration for Mermaid diagram types used across:
 * - DiagramPickerModal (document editor)
 * - ProjectDetailContent (Flow tab new diagram modal)
 * - FlowDiagramViewer (type badge display)
 */

import type { MermaidDiagramType } from '../types/panel-types';

/**
 * Configuration for each Mermaid diagram type
 */
export interface MermaidTypeConfig {
  /** Display label */
  label: string;
  /** Emoji icon for display */
  icon: string;
  /** Short description */
  description: string;
  /** Background color class for badges */
  bgColor: string;
  /** Text color class for badges */
  textColor: string;
}

/**
 * Mermaid DSL templates for each diagram type
 */
export const MERMAID_TEMPLATES: Record<MermaidDiagramType, string> = {
  flowchart: `flowchart TD
    A[Start] --> B[Process]
    B --> C{Decision}
    C -->|Yes| D[Action]
    C -->|No| E[End]`,

  sequence: `sequenceDiagram
    participant A as Client
    participant B as Server
    A->>B: Request
    B-->>A: Response`,

  state: `stateDiagram-v2
    [*] --> Idle
    Idle --> Processing: Start
    Processing --> Complete: Done
    Complete --> [*]`,

  er: `erDiagram
    CUSTOMER ||--o{ ORDER : places
    ORDER ||--|{ LINE-ITEM : contains
    PRODUCT ||--o{ LINE-ITEM : "ordered in"`,

  graph: `graph TD
    A[Start] --> B[End]`,

  classDiagram: `classDiagram
    class Animal {
        +String name
        +move()
    }
    class Dog {
        +bark()
    }
    Animal <|-- Dog`,

  gantt: `gantt
    title Project Schedule
    dateFormat YYYY-MM-DD
    
    section Phase 1
    Requirements    :a1, 2026-01-15, 14d
    Design          :a2, after a1, 10d
    
    section Phase 2
    Implementation  :b1, after a2, 21d
    Testing         :b2, after b1, 14d`,

  pie: `pie showData
    title Distribution
    "Category A" : 45
    "Category B" : 30
    "Category C" : 25`,

  timeline: `timeline
    title Project Milestones
    section 2026 Q1
        January : Requirements Complete
        February : Design Review
        March : CDR Approval
    section 2026 Q2
        April : Implementation Start
        May : Integration Testing
        June : SOI-4 Submission`,

  mindmap: `mindmap
  root((System))
    Hardware
      Processor
      Memory
      I/O
    Software
      Application
      OS
      Drivers
    Safety
      FMEA
      FTA
      SSA`,

  quadrantChart: `quadrantChart
    title Risk Assessment Matrix
    x-axis Low Impact --> High Impact
    y-axis Low Probability --> High Probability
    quadrant-1 Mitigate
    quadrant-2 Monitor
    quadrant-3 Accept
    quadrant-4 Transfer
    Risk A: [0.8, 0.7]
    Risk B: [0.3, 0.6]
    Risk C: [0.6, 0.2]`,

  requirement: `requirementDiagram
    requirement high_level_req {
        id: REQ-001
        text: System shall process data
        risk: high
        verifymethod: test
    }
    functionalRequirement derived_req {
        id: REQ-001.1
        text: Data shall be validated
        risk: medium
        verifymethod: inspection
    }
    high_level_req - derives -> derived_req`,

  c4Context: `C4Context
    title System Context Diagram
    
    Person(user, "User", "End user of the system")
    System(system, "Target System", "The system being developed")
    System_Ext(ext, "External System", "Third-party integration")
    
    Rel(user, system, "Uses")
    Rel(system, ext, "Queries")`,
};

/**
 * Display configuration for each Mermaid diagram type
 * Includes label, icon, description, and Tailwind color classes for badges
 */
export const MERMAID_TYPE_CONFIG: Record<MermaidDiagramType, MermaidTypeConfig> = {
  flowchart: {
    label: 'Flowchart',
    icon: '📊',
    description: 'Process flows, workflows',
    bgColor: 'bg-diagram-flowchart-bg',
    textColor: 'text-diagram-flowchart-text',
  },
  sequence: {
    label: 'Sequence',
    icon: '↔️',
    description: 'Message sequences',
    bgColor: 'bg-diagram-sequence-bg',
    textColor: 'text-diagram-sequence-text',
  },
  state: {
    label: 'State Machine',
    icon: '⏺️',
    description: 'State machines',
    bgColor: 'bg-diagram-state-bg',
    textColor: 'text-diagram-state-text',
  },
  er: {
    label: 'ER Diagram',
    icon: '🗃️',
    description: 'Entity relationships',
    bgColor: 'bg-diagram-er-bg',
    textColor: 'text-diagram-er-text',
  },
  graph: {
    label: 'Graph',
    icon: '🔗',
    description: 'Generic graph diagrams',
    bgColor: 'bg-diagram-graph-bg',
    textColor: 'text-diagram-graph-text',
  },
  classDiagram: {
    label: 'Class Diagram',
    icon: '📦',
    description: 'Class diagrams',
    bgColor: 'bg-info/10',
    textColor: 'text-info',
  },
  gantt: {
    label: 'Gantt Chart',
    icon: '📅',
    description: 'Project schedules',
    bgColor: 'bg-success/10',
    textColor: 'text-success',
  },
  pie: {
    label: 'Pie Chart',
    icon: '🥧',
    description: 'Distribution data',
    bgColor: 'bg-warning/10',
    textColor: 'text-warning',
  },
  timeline: {
    label: 'Timeline',
    icon: '📆',
    description: 'Milestones, events',
    bgColor: 'bg-primary/10',
    textColor: 'text-primary',
  },
  mindmap: {
    label: 'Mindmap',
    icon: '🧠',
    description: 'Concept mapping',
    bgColor: 'bg-secondary/50',
    textColor: 'text-secondary-foreground',
  },
  quadrantChart: {
    label: 'Quadrant Chart',
    icon: '📐',
    description: 'Risk matrices',
    bgColor: 'bg-destructive/10',
    textColor: 'text-destructive',
  },
  requirement: {
    label: 'Requirement',
    icon: '📋',
    description: 'Traceability diagrams',
    bgColor: 'bg-accent',
    textColor: 'text-accent-foreground',
  },
  c4Context: {
    label: 'C4 Context',
    icon: '🏛️',
    description: 'Architecture diagrams',
    bgColor: 'bg-muted',
    textColor: 'text-muted-foreground',
  },
};

/**
 * Ordered list of diagram types for UI display
 * Excludes 'graph' as it's essentially the same as 'flowchart'
 */
export const MERMAID_DIAGRAM_TYPES: MermaidDiagramType[] = [
  'flowchart',
  'sequence',
  'state',
  'er',
  'classDiagram',
  'gantt',
  'pie',
  'timeline',
  'mindmap',
  'quadrantChart',
  'requirement',
  'c4Context',
];
